import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check, Loader2, Pencil, RefreshCw, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { actOnLegalField, rescoreDealLegalFields } from '../../services/api/implementation/impl';
import {
  acceptVerdictFor,
  bandClasses,
  bandGuidance,
  bandLabel,
  fieldLabel,
  isFieldSettled,
  MATCH_BAND,
  noteRequiredForFieldVerdict
} from '../../../shared/utils/legalAccess';

/**
 * What the document said, beside what the CRM says, field by field.
 *
 * The reviewer's job is the middle column: everything that did not pass
 * cleanly needs a decision, and the four ways out — accept, accept with a
 * reason, read it yourself, or fix the CRM — are all on the row rather than
 * behind a menu.
 */
const LegalFieldEvidence = ({ dealId, fields = [], canDecide, onChange }) => {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState('');
  const [notes, setNotes] = useState({});
  // A decision made by mistake has to be correctable, or the only way back is
  // reopening the whole deal.
  const [reopened, setReopened] = useState({});

  const done = (message) => {
    toast.success(message);
    setEditing(null);
    setDraft('');
    setReopened({});
    onChange?.();
  };
  const fail = (err) =>
    toast.error(err?.response?.data?.message || err?.message || 'Action failed');

  const fieldMutation = useMutation({
    mutationFn: ({ id, action, body }) => actOnLegalField(id, action, body),
    onSuccess: (_r, v) => done(v.successMessage),
    onError: fail
  });

  const rescoreMutation = useMutation({
    mutationFn: () => rescoreDealLegalFields(dealId),
    onSuccess: () => done('Re-scored against the applicant records'),
    onError: fail
  });

  if (!fields.length) {
    return (
      <p className="px-5 py-4 text-sm text-gray-500">
        No field comparisons yet — they appear once a KYC document has been read.
      </p>
    );
  }

  const busy = fieldMutation.isPending || rescoreMutation.isPending;

  const decide = (row, verdict, successMessage) => {
    const note = (notes[row.id] || '').trim();
    if (noteRequiredForFieldVerdict(row.band, verdict) && note.length < 3) {
      toast.error(
        row.band === MATCH_BAND.UNREADABLE || row.band === MATCH_BAND.NO_REFERENCE
          ? 'Say why this is acceptable — nothing could be compared here'
          : 'Add a reason — it goes on the record'
      );
      return;
    }
    fieldMutation.mutate({
      id: row.id,
      action: 'verdict',
      body: { verdict, note: note || undefined },
      successMessage
    });
  };

  const saveManual = (row) => {
    if (!draft.trim()) {
      toast.error('Type the value as it appears on the document');
      return;
    }
    fieldMutation.mutate({
      id: row.id,
      action: 'manual',
      body: { value: draft.trim() },
      successMessage: 'Value recorded and re-scored'
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between px-5 pb-2 pt-4">
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
          Document against applicant record
        </h4>
        {canDecide ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => rescoreMutation.mutate()}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2A565E] hover:underline disabled:opacity-50">
            {rescoreMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Re-score
          </button>
        ) : null}
      </div>

      <ul className="divide-y divide-gray-100 border-t border-gray-100">
        {fields.map((row) => {
          const settled = isFieldSettled(row);
          const needsDecision = canDecide && row.band !== MATCH_BAND.PASS;
          return (
            <li key={row.id} className="px-5 py-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">
                    {fieldLabel(row.field)}
                    {row.manual_entry ? (
                      <span className="ml-2 text-[10px] font-medium uppercase tracking-wide text-gray-500">
                        read by hand
                      </span>
                    ) : null}
                  </p>
                  <dl className="mt-1 grid gap-x-6 gap-y-0.5 text-xs text-gray-600 sm:grid-cols-2">
                    <div className="flex gap-1.5">
                      <dt className="text-gray-400">Document</dt>
                      <dd className="break-all font-medium text-gray-800">
                        {row.extracted_value || '—'}
                      </dd>
                    </div>
                    <div className="flex gap-1.5">
                      <dt className="text-gray-400">Record</dt>
                      <dd className="break-all font-medium text-gray-800">
                        {row.reference_value || '—'}
                      </dd>
                    </div>
                  </dl>
                  <p className="mt-1 text-xs text-gray-500">{bandGuidance(row.band)}</p>
                  {row.verdict && row.verdict !== 'pending' ? (
                    <p className="mt-1 text-xs text-gray-500">
                      {row.verdict === 'rejected' ? 'Rejected' : 'Accepted'}
                      {row.reviewer_name ? ` by ${row.reviewer_name}` : ''}
                      {row.note ? ` — ${row.note}` : ''}
                      {canDecide ? (
                        <button
                          type="button"
                          onClick={() => setReopened({ ...reopened, [row.id]: true })}
                          className="ml-2 font-semibold text-[#2A565E] hover:underline">
                          Change
                        </button>
                      ) : null}
                    </p>
                  ) : null}
                </div>

                <span
                  className={`inline-flex shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${bandClasses(
                    row.band
                  )}`}>
                  {bandLabel(row.band)}
                  {row.band !== MATCH_BAND.PASS && row.score
                    ? ` · ${Number(row.score).toFixed(2)}`
                    : ''}
                </span>
              </div>

              {editing === row.id ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    autoFocus
                    placeholder="Value exactly as printed on the document"
                    className="min-w-[16rem] flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
                  />
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => saveManual(row)}
                    className="rounded-lg bg-[#2A565E] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1B3B41] disabled:opacity-50">
                    Save and re-score
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(null)}
                    className="rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100">
                    Cancel
                  </button>
                </div>
              ) : null}

              {needsDecision && (!settled || reopened[row.id]) && editing !== row.id ? (
                <div className="mt-2 space-y-2">
                  <input
                    value={notes[row.id] || ''}
                    onChange={(e) => setNotes({ ...notes, [row.id]: e.target.value })}
                    placeholder="Reason — required to accept a mismatch or reject"
                    className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-xs focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => decide(row, acceptVerdictFor(row.band), 'Field accepted')}
                      className="inline-flex items-center gap-1 rounded-lg border border-emerald-600/40 px-2.5 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-50 disabled:opacity-50">
                      <Check className="h-3.5 w-3.5" />
                      {row.band === MATCH_BAND.FAIL ? 'Accept with reason' : 'Accept'}
                    </button>

                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setEditing(row.id);
                        setDraft(row.extracted_value || '');
                      }}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                      <Pencil className="h-3.5 w-3.5" />
                      Read it myself
                    </button>

                    {row.band === MATCH_BAND.NO_REFERENCE && row.extracted_value ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          fieldMutation.mutate({
                            id: row.id,
                            action: 'accept-into-crm',
                            body: {},
                            successMessage: 'Applicant record updated'
                          })
                        }
                        className="inline-flex items-center gap-1 rounded-lg border border-sky-600/40 px-2.5 py-1.5 text-xs font-semibold text-sky-800 hover:bg-sky-50 disabled:opacity-50">
                        Fill the record from the document
                      </button>
                    ) : null}

                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => decide(row, 'rejected', 'Field rejected')}
                      className="inline-flex items-center gap-1 rounded-lg border border-rose-600/40 px-2.5 py-1.5 text-xs font-semibold text-rose-800 hover:bg-rose-50 disabled:opacity-50">
                      <X className="h-3.5 w-3.5" />
                      Reject
                    </button>
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default LegalFieldEvidence;
